/** NotaVera N9 (PRD §13 FR-OPS-04): types + pure helpers of the maintenance notices and the status page (ops.test.ts). */
export type Phase = 'UPCOMING' | 'ACTIVE' | 'ENDED' | 'CANCELLED';
export type ComponentStatus = 'OPERATIONAL' | 'DEGRADED' | 'DOWN' | 'MAINTENANCE' | 'NOT_CONFIGURED';
export type Overall = 'OPERATIONAL' | 'DEGRADED' | 'OUTAGE' | 'MAINTENANCE';

export interface BannerNotice { id: string; ref: string; kind: 'PLANNED' | 'UNPLANNED'; title: string; body: string; startsAt: string; endsAt: string; components: string[]; phase: Phase; duration: string }
export interface Notice extends BannerNotice { status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED'; notifyEna: boolean; announcedAt: string | null; recipients: number; emailed: number; completedAt: string | null; cancelledAt: string | null; cancelReason: string | null; createdAt: string }
export interface NoticeDetail extends Notice { events: { id: string; kind: string; detail: unknown; at: string }[] }
export interface PublicStatus { overall: Overall; updatedAt: string; components: { key: string; label: string; status: ComponentStatus }[]; maintenance: Omit<BannerNotice, 'id' | 'duration'>[] }

export const COMPONENTS: { key: string; label: string }[] = [
  { key: 'app', label: 'Web app' },
  { key: 'api', label: 'API' },
  { key: 'database', label: 'Database' },
  { key: 'book_duplicate', label: 'Notarial book duplicate' },
  { key: 'video', label: 'Video (REN sessions)' },
  { key: 'face_worker', label: 'Identity check (face match)' },
  { key: 'jobs', label: 'Background jobs' },
];
export const STATUS: Record<ComponentStatus, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }> = {
  OPERATIONAL: { label: 'Operational', tone: 'ok' },
  DEGRADED: { label: 'Degraded', tone: 'warn' },
  DOWN: { label: 'Down', tone: 'danger' },
  MAINTENANCE: { label: 'Maintenance', tone: 'info' },
  NOT_CONFIGURED: { label: 'Not monitored here', tone: 'neutral' },
};
export const OVERALL: Record<Overall, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' }> = {
  OPERATIONAL: { label: 'All systems operational', tone: 'ok' },
  DEGRADED: { label: 'Some services are degraded', tone: 'warn' },
  OUTAGE: { label: 'Service outage', tone: 'danger' },
  MAINTENANCE: { label: 'Maintenance in progress', tone: 'info' },
};
export const PHASE: Record<Phase, { label: string; tone: 'ok' | 'warn' | 'info' | 'neutral' }> = {
  UPCOMING: { label: 'Upcoming', tone: 'info' },
  ACTIVE: { label: 'In progress', tone: 'warn' },
  ENDED: { label: 'Ended', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const manila = (v: string) => new Date(v).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Manila' });
export const componentLabel = (k: string) => COMPONENTS.find((c) => c.key === k)?.label ?? k;

/** The banner line: what, when (Manila) and how long. */
export function bannerText(n: Pick<BannerNotice, 'kind' | 'title' | 'startsAt' | 'endsAt' | 'phase' | 'duration'>): string {
  if (n.phase === 'ACTIVE') return `${n.kind === 'UNPLANNED' ? 'Service disruption' : 'Maintenance in progress'}: ${n.title} — until about ${manila(n.endsAt)}.`;
  return `Planned maintenance: ${n.title} — ${manila(n.startsAt)}, about ${n.duration}.`;
}

/** Client-side mirror of the API's checks, so the form can say what is wrong before sending (the API checks again). */
export function noticeProblems(f: { kind: 'PLANNED' | 'UNPLANNED'; title: string; body: string; startsAt: string; endsAt: string; components: string[] }, now = new Date(), minHours = 48): string[] {
  const out: string[] = [];
  if (f.title.trim().length < 5) out.push('Title: at least 5 characters.');
  if (f.body.trim().length < 10) out.push('Say what is affected and what users should do.');
  const a = new Date(f.startsAt), b = new Date(f.endsAt);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return [...out, 'Start and expected end are required.'];
  if (b <= a) out.push('The expected end must be after the start.');
  if ((b.getTime() - a.getTime()) / 3_600_000 >= 15) out.push('A window of 15 hours or more is not allowed (bond, Guidelines III §8(b)).');
  if (f.kind === 'PLANNED' && (a.getTime() - now.getTime()) / 3_600_000 < minHours) out.push(`Planned work must be announced at least ${minHours} hours ahead.`);
  if (!f.components.length) out.push('Choose at least one affected component.');
  return out;
}
